import { motion } from "framer-motion";
import { Waves } from "lucide-react";import {
  OC_LIFESTYLE_VIDEO_URL,
  OC_HARBOR_HOME_URL,
  OC_GOLDEN_HOUR_HOME_URL,
  OC_PIER_SUNSET_URL,
} from "@/lib/assets";

export default function OCLifestyle() {
  return (
    <section
      id="oc-lifestyle"
      data-testid="oc-lifestyle-section"
      className="relative py-24 sm:py-32 bg-navy-900 overflow-hidden isolate"
    >
      {/* warm sunset + ocean glow accents */}
      <div className="absolute -top-24 right-0 h-[30rem] w-[30rem] rounded-full bg-gold-500/20 blur-3xl -z-10" />
      <div className="absolute bottom-[-6rem] -left-24 h-96 w-96 rounded-full bg-[#ff8a5c]/15 blur-3xl -z-10" />
      <div className="absolute top-1/3 left-1/2 h-80 w-80 rounded-full bg-[#38bdf8]/10 blur-3xl -z-10" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — copy + real photos */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 rounded-full glass-dark px-4 py-2 mb-6" data-testid="oc-lifestyle-overline">
              <Waves size={14} className="text-gold-400" />
              <span className="text-xs uppercase tracking-[0.22em] text-white/80">Orange County Living</span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl text-white leading-tight tracking-tight">
              Rooted in <em className="italic text-gold-400">Orange County</em>.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-xl">
              From the boat docks of Huntington Harbour to the palm-lined streets inland,
              we know these neighborhoods like home — because they are. Sun-warmed avenues,
              golden-hour skies, and homes with a lifetime of stories. We help families honor
              that legacy when it's time to sell.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4" data-testid="oc-lifestyle-gallery">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] ring-1 ring-white/10 group">
                <img
                  src={OC_HARBOR_HOME_URL}
                  alt="Waterfront home in Huntington Harbour, Orange County"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 to-transparent" />
                <span className="absolute bottom-3 left-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">
                  Huntington Harbour
                </span>
              </div>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] ring-1 ring-white/10 group">
                <img
                  src={OC_GOLDEN_HOUR_HOME_URL}
                  alt="Orange County coastal home at golden hour"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 to-transparent" />
                <span className="absolute bottom-3 left-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">
                  Golden-hour listings
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right — featured coastal video */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.12, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <video
                data-testid="oc-lifestyle-video"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={OC_PIER_SUNSET_URL}
                className="h-[420px] sm:h-[520px] w-full object-cover"
              >
                <source src={OC_LIFESTYLE_VIDEO_URL} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-transparent to-navy-900/10 pointer-events-none" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="glass-dark rounded-2xl px-5 py-3 inline-block">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-gold-400 font-semibold">
                    Coastal OC Lifestyle
                  </div>
                  <div className="mt-0.5 font-heading text-lg text-white">
                    Where luxury meets the shoreline
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -z-10 -bottom-8 -right-8 h-56 w-56 rounded-full bg-gold-500/20 blur-3xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
